var React = require('react');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

/**
 * Authentication Service with Context
...
*/

/**
 * Authentication Service with Context
 * 
 * Handles:
 * - Login (with password challenge support)
 * - Password challenge response
 * - Token management (access, refresh, id tokens)
 * - Token refresh
 * - Logout
 */

function _catch$2(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }
  if (result && result.then) {
    return result.then(void 0, recover);
  }
  return result;
}
var API_BASE_URL = '';

// Create context
var AuthContext = React.createContext(null);
var AuthServiceImpl = /*#__PURE__*/function () {
  function AuthServiceImpl() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    this.idToken = localStorage.getItem('idToken');

    // Safely parse user from localStorage
    try {
      var userString = localStorage.getItem('user');
      // Handle null, undefined, or the string "undefined"
      if (userString && userString !== 'undefined' && userString !== 'null') {
        this.user = JSON.parse(userString);
      } else {
        this.user = null;
      }
    } catch (err) {
      console.error('Error parsing user from localStorage:', err);
      this.user = null;
      // Clear the corrupted data
      localStorage.removeItem('user');
    }
  }

  /**
   * Login user with username and password
   * 
   * Returns:
   * - If successful: { user, idToken, accessToken, refreshToken, expiresIn }
   * - If NEW_PASSWORD_REQUIRED: { challengeName, session, userAttributes, username }
   * - Throws error if invalid credentials
   */
  var _proto = AuthServiceImpl.prototype;
  _proto.login = function login(username, password) {
    try {
      var _this = this;
      return Promise.resolve(_catch$2(function () {
        return Promise.resolve(fetch(API_BASE_URL + "/auth", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: username,
            password: password
          })
        })).then(function (response) {
          return Promise.resolve(response.json()).then(function (result) {
            if (!response.ok) {
              throw new Error(result.error || 'Login failed');
            }

            // Handle NEW_PASSWORD_REQUIRED challenge
            if (result.data.challengeName === 'NEW_PASSWORD_REQUIRED') {
              return {
                challengeName: 'NEW_PASSWORD_REQUIRED',
                session: result.data.session,
                userAttributes: result.data.userAttributes,
                username: username
              };
            }

            // Successful login - store tokens
            _this.setTokens(result.data);
            return result.data;
          });
        });
      }, function (error) {
        console.error('Login error:', error);
        throw error;
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Complete NEW_PASSWORD_REQUIRED challenge
   * 
   * Takes: username, newPassword, session (from login response)
   * Returns: { user, idToken, accessToken, refreshToken, expiresIn }
   */
;
  _proto.completePasswordChallenge = function completePasswordChallenge(username, newPassword, session) {
    try {
      var _this2 = this;
      return Promise.resolve(_catch$2(function () {
        return Promise.resolve(fetch(API_BASE_URL + "/auth/complete-password", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: username,
            newPassword: newPassword,
            session: session
          })
        })).then(function (response) {
          return Promise.resolve(response.json()).then(function (result) {
            if (!response.ok) {
              throw new Error(result.error || 'Failed to set password');
            }

            // Store tokens
            _this2.setTokens(result.data);
            return result.data;
          });
        });
      }, function (error) {
        console.error('Password challenge error:', error);
        throw error;
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Change password for authenticated user
   * 
   * Requires: old password (must be correct)
   */
;
  _proto.changePassword = function changePassword(oldPassword, newPassword) {
    try {
      var _this3 = this;
      return Promise.resolve(_catch$2(function () {
        return Promise.resolve(fetch(API_BASE_URL + "/auth/change", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': "Bearer " + _this3.accessToken
          },
          body: JSON.stringify({
            oldPassword: oldPassword,
            newPassword: newPassword
          })
        })).then(function (response) {
          return Promise.resolve(response.json()).then(function (result) {
            if (!response.ok) {
              throw new Error(result.error || 'Failed to change password');
            }
            return result.data;
          });
        });
      }, function (error) {
        console.error('Change password error:', error);
        throw error;
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Refresh access token using refresh token
   */
;
  _proto.refreshAccessToken = function refreshAccessToken() {
    try {
      var _this4 = this;
      return Promise.resolve(_catch$2(function () {
        return Promise.resolve(fetch(API_BASE_URL + "/auth/refresh", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            refreshToken: _this4.refreshToken
          })
        })).then(function (response) {
          return Promise.resolve(response.json()).then(function (result) {
            if (!response.ok) {
              throw new Error('Token refresh failed');
            }
            _this4.setTokens(result.data);
            return _this4.accessToken;
          });
        });
      }, function (error) {
        console.error('Token refresh error:', error);
        _this4.clearTokens();
        throw error;
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Store tokens and user info
   * Only updates provided values, preserves existing tokens during refresh
   */
;
  _proto.setTokens = function setTokens(data) {
    // Only update if values are provided
    if (data.idToken) {
      this.idToken = data.idToken;
      localStorage.setItem('idToken', data.idToken);
    }
    if (data.accessToken) {
      this.accessToken = data.accessToken;
      localStorage.setItem('accessToken', data.accessToken);
    }
    if (data.refreshToken) {
      this.refreshToken = data.refreshToken;
      localStorage.setItem('refreshToken', data.refreshToken);
    }
    if (data.user) {
      this.user = data.user;
      localStorage.setItem('user', JSON.stringify(data.user));
    }
  }

  /**
   * Clear tokens and user info
   */;
  _proto.clearTokens = function clearTokens() {
    this.idToken = null;
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  /**
   * Logout
   */;
  _proto.logout = function logout() {
    this.clearTokens();
  }

  /**
   * Get current access token
   */;
  _proto.getAccessToken = function getAccessToken() {
    return this.accessToken;
  }

  /**
   * Get current user
   */;
  _proto.getUser = function getUser() {
    return this.user;
  }

  /**
   * Check if user is authenticated
   */;
  _proto.isAuthenticated = function isAuthenticated() {
    return !!this.accessToken && !!this.user;
  };
  return AuthServiceImpl;
}(); // Singleton instance
var authService = new AuthServiceImpl();

/**
 * AuthProvider Component
 * 
 * Wrap your entire app with this to provide auth context
 * 
 * Usage:
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 */
function AuthProvider(_ref) {
  var children = _ref.children;
  var _useState = React.useState(authService.getUser()),
    user = _useState[0],
    setUser = _useState[1];
  var _useState2 = React.useState(authService.isAuthenticated()),
    isAuthenticated = _useState2[0],
    setIsAuthenticated = _useState2[1];

  // Initialize from localStorage on mount
  React.useEffect(function () {
    var savedUser = localStorage.getItem('user');
    var savedToken = localStorage.getItem('accessToken');
    if (savedUser && savedToken && savedUser !== 'undefined') {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Error parsing user from localStorage:', err);
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
      }
    }
  }, []);
  var login = React.useCallback(function (username, password) {
    try {
      return Promise.resolve(authService.login(username, password)).then(function (result) {
        if (result.challengeName !== 'NEW_PASSWORD_REQUIRED') {
          setUser(result.user);
          setIsAuthenticated(true);
        }
        return result;
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }, []);
  var completePasswordChallenge = React.useCallback(function (username, newPassword, session) {
    try {
      return Promise.resolve(authService.completePasswordChallenge(username, newPassword, session)).then(function (result) {
        setUser(result.user);
        setIsAuthenticated(true);
        return result;
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }, []);
  var changePassword = React.useCallback(function (oldPassword, newPassword) {
    try {
      return Promise.resolve(authService.changePassword(oldPassword, newPassword));
    } catch (e) {
      return Promise.reject(e);
    }
  }, []);
  var logout = React.useCallback(function () {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);
  var getAccessToken = React.useCallback(function () {
    return authService.getAccessToken();
  }, []);
  var value = {
    user: user,
    isAuthenticated: isAuthenticated,
    login: login,
    completePasswordChallenge: completePasswordChallenge,
    changePassword: changePassword,
    logout: logout,
    getAccessToken: getAccessToken
  };
  return /*#__PURE__*/React__default["default"].createElement(AuthContext.Provider, {
    value: value
  }, children);
}

/**
 * React hook for using auth service
 * 
 * Must be used inside AuthProvider
 * 
 * Usage:
 * const { user, isAuthenticated, login, logout } = useAuth();
 */
var useAuth = function useAuth() {
  var context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
};

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

/**
 * API Client
 *
 * Automatically handles:
 * - Adding Authorization header with valid token
 * - Refreshing expired tokens
 * - Parsing responses
 * - Error handling
 *
 * Usage:
 * const classes = await apiClient.get('/api/classes');
 * const newClass = await apiClient.post('/api/classes', { name: 'Class 1' });
 * const updated = await apiClient.put('/api/classes/123', { name: 'Updated' });
 * await apiClient.delete('/api/classes/123');
 */

function _catch$1(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }
  if (result && result.then) {
    return result.then(void 0, recover);
  }
  return result;
}
var BASE_URL = '';
function _finallyRethrows$1(body, finalizer) {
  try {
    var result = body();
  } catch (e) {
    return finalizer(true, e);
  }
  if (result && result.then) {
    return result.then(finalizer.bind(null, false), finalizer.bind(null, true));
  }
  return finalizer(false, result);
}
var APIClient = /*#__PURE__*/function () {
  function APIClient(baseUrl) {
    if (baseUrl === void 0) {
      baseUrl = BASE_URL;
    }
    this.baseUrl = baseUrl;
    this.isRefreshing = false;
    this.refreshQueue = [];
  }

  /**
   * Queue failed requests while token is refreshing
   */
  var _proto = APIClient.prototype;
  _proto.queueRequest = function queueRequest(callback) {
    var _this = this;
    return new Promise(function (resolve, reject) {
      _this.refreshQueue.push({
        callback: callback,
        resolve: resolve,
        reject: reject
      });
    });
  }

  /**
   * Process queued requests after token refresh
   */;
  _proto.processQueue = function processQueue(error) {
    if (error === void 0) {
      error = null;
    }
    this.refreshQueue.forEach(function (_ref) {
      var callback = _ref.callback,
        resolve = _ref.resolve,
        reject = _ref.reject;
      if (error) {
        reject(error);
      } else {
        callback().then(resolve)["catch"](reject);
      }
    });
    this.refreshQueue = [];
  }

  /**
   * Prepare request with authentication headers
   */;
  _proto.getHeaders = function getHeaders(additionalHeaders, authRequired) {
    if (additionalHeaders === void 0) {
      additionalHeaders = {};
    }
    try {
      var accessToken = authService.getAccessToken();
      if (authRequired && (!accessToken || accessToken === 'undefined' || accessToken === 'null')) {
        throw new Error('No valid authentication token available');
      }
      return Promise.resolve(_extends({
        'Content-Type': 'application/json'
      }, authRequired ? {
        'Authorization': "Bearer " + accessToken
      } : {}, additionalHeaders));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Handle API response
   */
;
  _proto.handleResponse = function handleResponse(response) {
    try {
      return Promise.resolve(response.json()).then(function (data) {
        if (!response.ok) {
          // Log detailed error info
          console.error('API Error:', {
            status: response.status,
            statusText: response.statusText,
            data: data
          });

          // Throw error with response data
          var error = new Error(data.error || data.message || response.statusText);
          error.status = response.status;
          error.data = data;
          throw error;
        }

        // Unwrap response - API returns { data: {...} }
        return data.data || data;
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Attempt to refresh the access token
   */
;
  _proto.refreshToken = function refreshToken() {
    try {
      var _this2 = this;
      return Promise.resolve(_catch$1(function () {
        var refreshToken = localStorage.getItem('refreshToken');

        // Safely handle corrupted localStorage values
        if (!refreshToken || refreshToken === 'undefined' || refreshToken === 'null') {
          throw new Error('No valid refresh token available');
        }
        return Promise.resolve(fetch(_this2.baseUrl + "/auth/refresh", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            refreshToken: refreshToken
          })
        })).then(function (response) {
          return Promise.resolve(response.json()).then(function (data) {
            if (!response.ok) {
              throw new Error(data.error || 'Token refresh failed');
            }

            // Safely handle response data
            if (!data.data || !data.data.accessToken) {
              throw new Error('Invalid refresh response - missing accessToken');
            }

            // Update tokens in localStorage with validation
            if (data.data.accessToken) {
              localStorage.setItem('accessToken', data.data.accessToken);
            }
            if (data.data.refreshToken) {
              localStorage.setItem('refreshToken', data.data.refreshToken);
            }
            if (data.data.idToken) {
              localStorage.setItem('idToken', data.data.idToken);
            }

            // Also update AuthService instance
            authService.setTokens(data.data);
            return data.data.accessToken;
          });
        });
      }, function (error) {
        console.error('Token refresh failed:', error);

        // Clear all tokens on refresh failure
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        authService.clearTokens();

        // Redirect to login
        window.location.href = '/';
        throw error;
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Make request with automatic token refresh on 401
   */
;
  _proto.makeRequest = function makeRequest(method, endpoint, body, options, authRequired) {
    if (body === void 0) {
      body = null;
    }
    if (options === void 0) {
      options = {};
    }
    if (authRequired === void 0) {
      authRequired = true;
    }
    try {
      var _this3 = this;
      return Promise.resolve(_catch$1(function () {
        console.log("Making request: " + method + " " + endpoint, authRequired);
        return Promise.resolve(_this3.getHeaders(options.headers, authRequired)).then(function (headers) {
          var config = _extends({
            method: method,
            headers: headers
          }, options);
          if (body) {
            config.body = JSON.stringify(body);
          }
          return Promise.resolve(fetch("" + _this3.baseUrl + endpoint, config)).then(function (response) {
            var _exit;
            function _temp4(_result2) {
              return _exit ? _result2 : _this3.handleResponse(response);
            }
            var _temp3 = function () {
              if (response.status === 401) {
                if (!_this3.isRefreshing) {
                  var _temp2 = function _temp2(_result) {
                    if (_exit) return _result;
                    // Now retry THIS request with the new token
                    console.log("Retrying " + method + " " + endpoint + " after token refresh");
                    return Promise.resolve(_this3.getHeaders(options.headers, authRequired)).then(function (newHeaders) {
                      var retryConfig = _extends({
                        method: method,
                        headers: newHeaders
                      }, options);
                      if (body) {
                        retryConfig.body = JSON.stringify(body);
                      }
                      return Promise.resolve(fetch("" + _this3.baseUrl + endpoint, retryConfig)).then(function (retryResponse) {
                        var _this3$handleResponse = _this3.handleResponse(retryResponse);
                        _exit = 1;
                        return _this3$handleResponse;
                      });
                    });
                  };
                  // This is the first request to hit 401 - start the refresh
                  _this3.isRefreshing = true;
                  var _temp = _finallyRethrows$1(function () {
                    return _catch$1(function () {
                      console.log('Token expired, refreshing...');
                      return Promise.resolve(_this3.refreshToken()).then(function () {
                        console.log('Token refreshed successfully');
                        // Process any queued requests that accumulated during refresh
                        _this3.processQueue();
                      });
                    }, function (error) {
                      console.error('Token refresh failed, rejecting queue:', error);
                      // Reject all queued requests
                      _this3.processQueue(error);
                      throw error;
                    });
                  }, function (_wasThrown, _result) {
                    _this3.isRefreshing = false;
                    if (_wasThrown) throw _result;
                    return _result;
                  });
                  return _temp && _temp.then ? _temp.then(_temp2) : _temp2(_temp);
                } else {
                  // A refresh is already in progress - queue this request for retry
                  console.log(method + " " + endpoint + " queued for retry during refresh");
                  var _this3$queueRequest = _this3.queueRequest(function () {
                    try {
                      console.log("Retrying queued " + method + " " + endpoint);
                      return Promise.resolve(_this3.getHeaders(options.headers, authRequired)).then(function (newHeaders) {
                        var retryConfig = _extends({
                          method: method,
                          headers: newHeaders
                        }, options);
                        if (body) {
                          retryConfig.body = JSON.stringify(body);
                        }
                        return Promise.resolve(fetch("" + _this3.baseUrl + endpoint, retryConfig)).then(function (retryResponse) {
                          return _this3.handleResponse(retryResponse);
                        });
                      });
                    } catch (e) {
                      return Promise.reject(e);
                    }
                  });
                  _exit = 1;
                  return _this3$queueRequest;
                }
              }
            }();
            // Handle 401 with token refresh
            return _temp3 && _temp3.then ? _temp3.then(_temp4) : _temp4(_temp3);
          });
        });
      }, function (error) {
        console.error(method + " " + endpoint + " failed:", error);
        throw error;
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * GET request
   */
;
  _proto.get = function get(endpoint, options, authRequired) {
    if (options === void 0) {
      options = {};
    }
    try {
      var _this4 = this;
      return Promise.resolve(_this4.makeRequest('GET', endpoint, null, options, authRequired));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * POST request
   */
;
  _proto.post = function post(endpoint, body, options, authRequired) {
    if (body === void 0) {
      body = {};
    }
    if (options === void 0) {
      options = {};
    }
    try {
      var _this5 = this;
      return Promise.resolve(_this5.makeRequest('POST', endpoint, body, options, authRequired));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * PUT request (update)
   */
;
  _proto.put = function put(endpoint, body, options, authRequired) {
    if (body === void 0) {
      body = {};
    }
    if (options === void 0) {
      options = {};
    }
    try {
      var _this6 = this;
      return Promise.resolve(_this6.makeRequest('PUT', endpoint, body, options, authRequired));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * DELETE request
   */
;
  _proto["delete"] = function _delete(endpoint, options, authRequired) {
    if (options === void 0) {
      options = {};
    }
    try {
      var _this7 = this;
      return Promise.resolve(_this7.makeRequest('DELETE', endpoint, null, options, authRequired));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Upload file
   */
;
  _proto.uploadFile = function uploadFile(endpoint, file, additionalData, options) {
    if (additionalData === void 0) {
      additionalData = {};
    }
    if (options === void 0) {
      options = {};
    }
    try {
      var _this8 = this;
      return Promise.resolve(_catch$1(function () {
        var accessToken = authService.getAccessToken();
        if (!accessToken) {
          throw new Error('No valid authentication token available');
        }
        var formData = new FormData();
        formData.append('file', file);

        // Add additional fields to form data
        Object.entries(additionalData).forEach(function (_ref2) {
          var key = _ref2[0],
            value = _ref2[1];
          formData.append(key, value);
        });
        return Promise.resolve(fetch("" + _this8.baseUrl + endpoint, _extends({
          method: 'POST',
          headers: {
            'Authorization': "Bearer " + accessToken
          },
          body: formData
        }, options))).then(function (response) {
          var _exit2;
          function _temp9(_result4) {
            return _exit2 ? _result4 : Promise.resolve(_this8.handleResponse(response));
          }
          var _temp8 = function () {
            if (response.status === 401) {
              var _temp7 = function _temp7() {
                // Retry file upload with new token
                var _this8$queueRequest = _this8.queueRequest(function () {
                  try {
                    var newAccessToken = authService.getAccessToken();
                    var retryFormData = new FormData();
                    retryFormData.append('file', file);
                    Object.entries(additionalData).forEach(function (_ref3) {
                      var key = _ref3[0],
                        value = _ref3[1];
                      retryFormData.append(key, value);
                    });
                    return Promise.resolve(fetch("" + _this8.baseUrl + endpoint, _extends({
                      method: 'POST',
                      headers: {
                        'Authorization': "Bearer " + newAccessToken
                      },
                      body: retryFormData
                    }, options))).then(function (retryResponse) {
                      return _this8.handleResponse(retryResponse);
                    });
                  } catch (e) {
                    return Promise.reject(e);
                  }
                });
                _exit2 = 1;
                return _this8$queueRequest;
              };
              var _temp6 = function () {
                if (!_this8.isRefreshing) {
                  _this8.isRefreshing = true;
                  var _temp5 = _finallyRethrows$1(function () {
                    return _catch$1(function () {
                      return Promise.resolve(_this8.refreshToken()).then(function () {
                        _this8.processQueue();
                      });
                    }, function (error) {
                      // Don't re-throw - let processQueue reject queued requests via promise
                      _this8.processQueue(error);
                    });
                  }, function (_wasThrown2, _result3) {
                    _this8.isRefreshing = false;
                    if (_wasThrown2) throw _result3;
                    return _result3;
                  });
                  if (_temp5 && _temp5.then) return _temp5.then(function () {});
                }
              }();
              return _temp6 && _temp6.then ? _temp6.then(_temp7) : _temp7(_temp6);
            }
          }();
          // Handle 401 with token refresh for file uploads
          return _temp8 && _temp8.then ? _temp8.then(_temp9) : _temp9(_temp8);
        });
      }, function (error) {
        console.error("File upload to " + endpoint + " failed:", error);
        throw error;
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  };
  return APIClient;
}();
var apiClient = new APIClient();

var getAppTheme = function getAppTheme() {
  try {
    return Promise.resolve(apiClient.get("/api/config/appTheme", {}, false)).then(function (appTheme) {
      console.log('fetched app theme', appTheme);
      return appTheme;
    });
  } catch (e) {
    return Promise.reject(e);
  }
};
var getConfigByTypeAndName = function getConfigByTypeAndName(type, name) {
  try {
    console.log('fetching config by type', type, 'and name', name);
    return Promise.resolve(apiClient.get("/api/config/" + type + "/" + name, {}, false));
  } catch (e) {
    return Promise.reject(e);
  }
};
var getMultipleConfigsByTypeAndName = function getMultipleConfigsByTypeAndName(typesAndNames) {
  try {
    return Promise.resolve(apiClient.post("/api/config/multiple", typesAndNames, {}, false)).then(function (configs) {
      console.log('fetched multiple configs', typesAndNames, configs);
      return configs;
    });
  } catch (e) {
    return Promise.reject(e);
  }
};
var SharedService = {
  getAppTheme: getAppTheme,
  getConfigByTypeAndName: getConfigByTypeAndName,
  getMultipleConfigsByTypeAndName: getMultipleConfigsByTypeAndName
};

var appName = "Superclasses";
var colours = {
	primaryDark: "#555555",
	primaryLight: "#d3d3d3",
	buttonBg: "#555555",
	buttonText: "#ffffff",
	success: "#666666",
	danger: "#777777",
	warning: "#888888",
	info: "#999999",
	light: "#f0f0f0",
	dark: "#333333",
	navFont: "#999999",
	pageFont: "#333333",
	bodyBackground: "#e8e8e8",
	listcardBackground: "#00000088"
};
var logo = "logo-superclasses.png";
var baseTheme = {
	appName: appName,
	colours: colours,
	logo: logo
};

// Create the Theme Context

function _catch(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }
  if (result && result.then) {
    return result.then(void 0, recover);
  }
  return result;
}
var ThemeContext = React.createContext();

// Theme Provider Component

function _finallyRethrows(body, finalizer) {
  try {
    var result = body();
  } catch (e) {
    return finalizer(true, e);
  }
  if (result && result.then) {
    return result.then(finalizer.bind(null, false), finalizer.bind(null, true));
  }
  return finalizer(false, result);
} // Custom hook to use the Theme Context
var ThemeProvider = function ThemeProvider(_ref) {
  var children = _ref.children;
  var _useState = React.useState(baseTheme),
    appTheme = _useState[0],
    setAppTheme = _useState[1];
  var _useState2 = React.useState(true),
    themeLoading = _useState2[0],
    setThemeLoading = _useState2[1];
  var _useState3 = React.useState(null),
    themeError = _useState3[0],
    setThemeError = _useState3[1];
  React.useEffect(function () {
    var fetchTheme = function fetchTheme() {
      try {
        var _temp = _finallyRethrows(function () {
          return _catch(function () {
            return Promise.resolve(SharedService.getAppTheme()).then(function (theme) {
              setAppTheme(theme);
              setThemeError(null);
            });
          }, function (error) {
            console.error('Error fetching app theme:', error);
            setThemeError(error);
            // Set a default theme if fetch fails
            setAppTheme(baseTheme);
          });
        }, function (_wasThrown, _result) {
          setThemeLoading(false);
          if (_wasThrown) throw _result;
          return _result;
        });
        return Promise.resolve(_temp && _temp.then ? _temp.then(function () {}) : void 0);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    fetchTheme();
  }, []);
  var value = {
    appTheme: appTheme,
    themeLoading: themeLoading,
    themeError: themeError
  };
  return /*#__PURE__*/React__default["default"].createElement(ThemeContext.Provider, {
    value: value
  }, children);
};
var useTheme = function useTheme() {
  var context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Hook to apply theme CSS custom properties to the document root
 * This makes theme colors available throughout the app including the login page
 */
var useThemeStyles = function useThemeStyles() {
  var _useTheme = useTheme(),
    appTheme = _useTheme.appTheme;
  React.useEffect(function () {
    if (appTheme != null && appTheme.appName) {
      document.title = appTheme.appName;
    }

    // Set CSS custom properties based on theme
    if (appTheme != null && appTheme.colours) {
      var root = document.documentElement;
      root.style.setProperty('--primary-light-color', appTheme.colours.primaryLight);
      root.style.setProperty('--primary-dark-color', appTheme.colours.primaryDark);
      root.style.setProperty('--primary-color-semitransparent', appTheme.colours.primaryDark + '88');
      root.style.setProperty('--primary-color-semitransparent2', appTheme.colours.primaryDark + '50');
      root.style.setProperty('--primary-color-semitransparent3', appTheme.colours.primaryDark + '35');
      root.style.setProperty('--primary-color-semitransparent4', appTheme.colours.primaryDark + '1A');
      root.style.setProperty('--button-bg-color', appTheme.colours.buttonBg);
      root.style.setProperty('--button-text-color', appTheme.colours.buttonText);
      root.style.setProperty('--nav-font-color-semitransparent', appTheme.colours.navFont + '88');
      root.style.setProperty('--nav-font-color', appTheme.colours.navFont);
      root.style.setProperty('--page-font-color', appTheme.colours.pageFont);
      root.style.setProperty('--body-background-color', appTheme.colours.bodyBackground);
      root.style.setProperty('--listcard-background-color', appTheme.colours.listcardBackground);
      root.style.setProperty('--success-color', appTheme.colours.success);
      root.style.setProperty('--danger-color', appTheme.colours.danger);
      root.style.setProperty('--warning-color', appTheme.colours.warning);
      root.style.setProperty('--info-color', appTheme.colours.info);
    }
  }, [appTheme]);
};

exports.AuthProvider = AuthProvider;
exports.AuthService = authService;
exports.SharedService = SharedService;
exports.ThemeProvider = ThemeProvider;
exports.apiClient = apiClient;
exports.useAuth = useAuth;
exports.useTheme = useTheme;
exports.useThemeStyles = useThemeStyles;
//# sourceMappingURL=index.js.map
